import { useEffect, useState, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import React from "react";
import OneSignal from 'react-onesignal';

// Pages
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import PostRound from "./pages/PostRound";
import RoundHistory from "./pages/RoundHistory";
import Membership from "./pages/Membership";
import PaymentSuccess from "./pages/PaymentSuccess";
import Profile from "./pages/Profile";
import Leaderboard from "./pages/Leaderboard";
import BundleStories from "./pages/BundleStories";
import PostStory from "./pages/PostStory";
import Events from "./pages/Events";
import CreateEvent from "./pages/CreateEvent";
import GolfTrips from "./pages/GolfTrips";
import BookTeeTimes from "./pages/BookTeeTimes";
import Admin from "./pages/Admin";
import AdminVideoMaker from "./pages/AdminVideoMaker";
import AdminNewsManager from "./pages/AdminNewsManager";
import AdminAnnouncements from "./pages/AdminAnnouncements";
import ManageRounds from "./pages/ManageRounds";
import ManageBundleWins from "./pages/ManageBundleWins";
import Chat from "./pages/Chat";
import NearbyGolfers from "./pages/NearbyGolfers";
import MemberDirectory from "./pages/MemberDirectory";
import LiveScorecard from "./pages/LiveScorecard";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import PublicRecap from "./pages/PublicRecap";
import CodeOfConduct from "./pages/CodeOfConduct";
import GearExchange from "./pages/GearExchange";
import WinningPostRules from "./pages/WinningPostRules";
import WelcomePage from "./pages/WelcomePage";
import TournamentProcessor from "./pages/TournamentProcessor";
import AdminRecruitLanding from "./pages/AdminRecruitLanding";
import FundraiserLanding from "./pages/FundraiserLanding";
import SponsorLanding from "./pages/SponsorLanding";
import SponsorMarketplace from "./pages/SponsorMarketplace";
import BundleTV from "./pages/BundleTV";
import SocialFeed from "./pages/SocialFeed";
import SmartRound from "./pages/SmartRound";
import CourseMapper from "./pages/CourseMapper";
import GoLive from "./pages/GoLive";
import BusinessNetwork from "./pages/BusinessNetwork";

// Components
import { Toaster } from "./components/ui/sonner";
import { InactiveOverlay } from "./components/InactiveOverlay";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import NewVersionPrompt from "./components/NewVersionPrompt";

// OneSignal App ID
const ONESIGNAL_APP_ID = '9d70bd81-970a-4d7b-b1a6-820c0b9452c7';

// Auto-detect API URL based on current domain
const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    return `${backendUrl}/api`;
  }
  // Use same origin + /api for production
  return `${window.location.origin}/api`;
};

export const API = getApiUrl();

// Helper function to get auth headers for API calls
export const getAuthHeaders = () => {
  const token = localStorage.getItem('session_token');
  if (token) {
    return { 'Authorization': `Bearer ${token}` };
  }
  return {};
};

/**
 * Safely convert a FastAPI/Pydantic error `detail` payload to a display string.
 * FastAPI 422 responses return `detail` as an array of validation objects like
 * [{type, loc, msg, input, url}, ...] — passing that directly to toast.error /
 * JSX children crashes React ("Objects are not valid as a React child").
 * This helper flattens any shape to a safe string.
 */
export const formatApiError = (detail, fallback = 'Something went wrong') => {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    // Pydantic validation error array
    const msgs = detail
      .map((d) => {
        if (typeof d === 'string') return d;
        if (d && typeof d === 'object') return d.msg || d.message || JSON.stringify(d);
        return String(d);
      })
      .filter(Boolean);
    return msgs.length ? msgs.join('; ') : fallback;
  }
  if (typeof detail === 'object') {
    return detail.msg || detail.message || fallback;
  }
  return String(detail) || fallback;
};

// Auth Context
export const AuthContext = React.createContext(null);

// Initialize OneSignal
let oneSignalInitialized = false;
const initOneSignal = async (userId = null) => {
  if (oneSignalInitialized || typeof window === 'undefined') return;
  
  try {
    await OneSignal.init({
      appId: ONESIGNAL_APP_ID,
      allowLocalhostAsSecureOrigin: true,
      notifyButton: {
        enable: false,  // Disabled — was overlapping mobile bottom nav and capturing scroll touches
      },
      promptOptions: {
        slidedown: {
          prompts: [{
            type: 'push',
            autoPrompt: false,  // We trigger our own branded prompt component
            text: {
              actionMessage: "Get notified about game invites and club activity",
              acceptButton: "Allow",
              cancelButton: "Later"
            }
          }]
        }
      }
    });
    oneSignalInitialized = true;
    
    // If user is logged in, set their external ID
    if (userId) {
      await OneSignal.login(userId);
    }
    
    console.log('OneSignal initialized successfully');
  } catch (error) {
    console.error('OneSignal initialization error:', error);
  }
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Helper to get auth headers (supports both cookie and localStorage token)
  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    if (token) {
      return { 'Authorization': `Bearer ${token}` };
    }
    return {};
  };

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        // Initialize OneSignal with user ID
        initOneSignal(userData.user_id);
      } else {
        setUser(null);
        // Clear stored token if auth fails
        localStorage.removeItem('session_token');
        // Initialize OneSignal without user ID
        initOneSignal();
      }
    } catch (error) {
      setUser(null);
      initOneSignal();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();

    // Clear app badge when the app is opened/focused
    const clearBadge = () => {
      if (navigator.clearAppBadge) {
        navigator.clearAppBadge().catch(() => {});
      }
      // Also reset IndexedDB badge count
      try {
        const req = indexedDB.open('bundle-badge-db', 1);
        req.onsuccess = () => {
          try {
            const db = req.result;
            const tx = db.transaction('badge', 'readwrite');
            tx.objectStore('badge').put({ id: 'count', value: 0 });
          } catch {}
        };
      } catch {}
    };

    clearBadge();
    window.addEventListener('focus', clearBadge);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') clearBadge();
    });

    return () => {
      window.removeEventListener('focus', clearBadge);
    };
  }, [checkAuth]);

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      // Logout from OneSignal
      if (oneSignalInitialized) {
        await OneSignal.logout();
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Clear stored token
    localStorage.removeItem('session_token');
    setUser(null);
  };

  // Custom setUser that also stores the session token
  const setUserWithToken = (userData) => {
    if (userData && userData.session_token) {
      localStorage.setItem('session_token', userData.session_token);
      // Remove token from user object before storing in state
      const { session_token, ...userWithoutToken } = userData;
      setUser(userWithoutToken);
    } else {
      setUser(userData);
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser: setUserWithToken, loading, logout, checkAuth, getAuthHeaders }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const { setUser } = React.useContext(AuthContext);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    
    if (!sessionIdMatch) {
      navigate('/');
      return;
    }

    const sessionId = sessionIdMatch[1];

    const exchangeSession = async () => {
      try {
        const response = await fetch(`${API}/auth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId })
        });

        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          navigate('/dashboard', { state: { user: userData } });
        } else {
          navigate('/');
        }
      } catch (error) {
        console.error('Auth error:', error);
        navigate('/');
      }
    };

    exchangeSession();
  }, [location, navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-bundle-cream">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-bundle-green mx-auto mb-4"></div>
        <p className="text-bundle-green font-body">Signing you in...</p>
      </div>
    </div>
  );
};

// App Router
const AppRouter = () => {
  const location = useLocation();
  const { user, loading } = React.useContext(AuthContext);

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Show inactive overlay for expired trial users (allow landing page and legal pages)
  const publicPaths = ['/', '/terms', '/privacy', '/code-of-conduct', '/winning-post-rules', '/welcome'];
  const isPublicPath = publicPaths.includes(location.pathname) || location.pathname.startsWith('/recap/');

  if (!loading && user && user.membership_status === 'inactive' && !isPublicPath) {
    return <InactiveOverlay />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/post-round" element={<PostRound />} />
      <Route path="/rounds" element={<RoundHistory />} />
      <Route path="/membership" element={<Membership />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/leaderboard" element={<Leaderboard />} />
      <Route path="/stories" element={<BundleStories />} />
      <Route path="/post-story" element={<PostStory />} />
      <Route path="/events" element={<Events />} />
      <Route path="/create-event" element={<CreateEvent />} />
      <Route path="/events/edit/:eventId" element={<CreateEvent />} />
      <Route path="/golf-trips" element={<GolfTrips />} />
      <Route path="/book-tee-times" element={<BookTeeTimes />} />
      <Route path="/chat" element={<Chat />} />
      <Route path="/nearby-golfers" element={<NearbyGolfers />} />
      <Route path="/members" element={<MemberDirectory />} />
      <Route path="/live-round/:liveRoundId" element={<LiveScorecard />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/admin/video-maker" element={<AdminVideoMaker />} />
      <Route path="/admin/news" element={<AdminNewsManager />} />
      <Route path="/admin/announcements" element={<AdminAnnouncements />} />
      <Route path="/admin/rounds" element={<ManageRounds />} />
      <Route path="/admin/bundle-wins" element={<ManageBundleWins />} />
      <Route path="/gear" element={<GearExchange />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/recap/:shareId" element={<PublicRecap />} />
      <Route path="/code-of-conduct" element={<CodeOfConduct />} />
      <Route path="/winning-post-rules" element={<WinningPostRules />} />
      <Route path="/welcome" element={<WelcomePage />} />
      {/* Legacy redirects — Tournaments/Calendar pages removed in favor of unified /events */}
      <Route path="/tournaments" element={<Navigate to="/events" replace />} />
      <Route path="/tournament/:id" element={<Navigate to="/events" replace />} />
      <Route path="/calendar" element={<Navigate to="/events" replace />} />
      <Route path="/bundle-tv" element={<BundleTV />} />
      <Route path="/feed" element={<SocialFeed />} />
      <Route path="/become-admin" element={<AdminRecruitLanding />} />
      <Route path="/fundraiser" element={<FundraiserLanding />} />
      <Route path="/sponsors" element={<SponsorLanding />} />
      <Route path="/sponsor-marketplace" element={<SponsorMarketplace />} />
      <Route path="/smart-round" element={<SmartRound />} />
      <Route path="/admin/course-mapper" element={<CourseMapper />} />
      <Route path="/tournament-processor" element={<TournamentProcessor />} />
      <Route path="/go-live" element={<GoLive />} />
      <Route path="/go-live/:momentId" element={<GoLive />} />
      <Route path="/network" element={<BusinessNetwork />} />
    </Routes>
  );
};

function App() {
  // Global interceptor: force all external links to open in system browser (PWA fix)
  useEffect(() => {
    const handleClick = (e) => {
      // Skip if this is a programmatic click from our own fallback
      if (e.__externalHandled) return;
      const anchor = e.target.closest('a[href]');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href) return;
      // Only intercept external links (http/https that aren't our own domain)
      if (href.startsWith('http') && !href.includes(window.location.hostname)) {
        e.preventDefault();
        e.stopPropagation();
        window.open(href, '_blank', 'noopener,noreferrer');
      }
    };
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="min-h-screen bg-bundle-cream">
          <PWAInstallPrompt />
          <NewVersionPrompt />
          <AppRouter />
          <Toaster position="top-right" />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
